import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import rawData from "./data.json";

const ScatterJitterPlot = () => {
  const [currentView, setCurrentView] = useState("all"); // "all", "low", "high"

  const filterData = (data, range) => {
    if (range === "all") return data;

    const filtered = {};
    Object.keys(data).forEach((key) => {
      filtered[key] = data[key].filter((item) => {
        const val = item.medicare_percentage;
        return range === "low" ? val <= 1000 : val > 1000;
      });
    });
    return filtered;
  };

  const getMaxValue = (data) => {
    let max = 0;
    Object.values(data).forEach((arr) => {
      arr.forEach((item) => {
        if (item.medicare_percentage > max) max = item.medicare_percentage;
      });
    });
    return max;
  };

  const getYAxisConfig = (range, filteredData) => {
    const baseYAxis = {
      type: "linear",
      title: {
        text: "Medicare Percentage",
        style: {
          color: "#64748B",
          fontSize: "14px",
          fontWeight: "600",
          fontFamily: "Roboto Flex",
        },
      },
      labels: {
        style: { color: "#64748B", fontSize: "12px", fontWeight: "500" },
      },
      gridLineColor: "#F8FAFC",
      gridLineWidth: 1,
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
    };

    switch (range) {
      case "all":
        return {
          ...baseYAxis,
          min: 0,
          max: getMaxValue(filteredData),
          tickInterval: 100,
        };
      case "low":
        return { ...baseYAxis, min: 0, max: 1000 };
      case "high":
        return { ...baseYAxis, min: 1000, max: getMaxValue(filteredData) };
      default:
        return baseYAxis;
    }
  };

  const variation3Config = {
    colors: ["#93C5FD", "#6EE7B7", "#FCD34D", "#C4B5FD", "#FCA5A5", "#67E8F9"],
    markerType: "circle",
    markerRadius: 5,
    jitter: { x: 0.35, y: 0 },
  };

  useEffect(() => {
    const filteredData = filterData(rawData, currentView);
    const categories = Object.keys(filteredData);
    const yAxisConfig = getYAxisConfig(currentView, filteredData);

    const series = categories.map((cat, index) => {
      const seriesColor =
        variation3Config.colors[index % variation3Config.colors.length];
      const data = filteredData[cat].map((item) => ({
        x: index,
        y: item.medicare_percentage,
        negotiated: item.negotiated_rates,
        provider: item.provider_name,
      }));

      return {
        name: cat,
        data,
        color: seriesColor,
        marker: {
          symbol: variation3Config.markerType,
          radius: variation3Config.markerRadius,
          fillColor: seriesColor,
          lineColor: "rgba(255, 255, 255, 0.8)",
          lineWidth: 1,
          states: {
            hover: { enabled: true, lineColor: "#FFFFFF", lineWidth: 3 },
          },
        },
      };
    });

    const getSubtitle = () => {
      switch (currentView) {
        case "low":
          return "Data range: 0-1000";
        case "high":
          return "Data range: 1000+";
        default:
          return "All data points";
      }
    };

    Highcharts.chart("scatter-jitter-container", {
      chart: {
        type: "scatter",
        zoomType: "xy",
        height: 700,
        backgroundColor: "#FFFFFF",
        plotBackgroundColor: "rgba(255, 255, 255, 0.9)",
        spacing: [40, 20, 40, 20],
        borderRadius: 8,
        style: { fontFamily: "'Roboto Flex', sans-serif" },
      },
      navigation: { buttonOptions: { enabled: false } },
      credits: { enabled: false },
      legend: { enabled: false },
      title: {
        text: "Medicare Percentage - Cigna",
        style: { color: "#1E293B", fontSize: "24px", fontWeight: "700" },
        margin: 30,
        align: "left",
      },
      subtitle: {
        text: `Medicare percentage across different billing codes • ${getSubtitle()}`,
        style: { color: "#64748B", fontSize: "14px", fontWeight: "400" },
        align: "left",
      },
      xAxis: {
        categories: categories,
        title: {
          text: "Medical Billing Codes",
          style: { color: "#64748B", fontSize: "14px", fontWeight: "600" },
        },
        labels: {
          style: { color: "#64748B", fontSize: "12px", fontWeight: "500" },
        },
        gridLineColor: "#F1F5F9",
        gridLineWidth: 1,
        lineColor: "#E2E8F0",
        tickColor: "#E2E8F0",
      },
      yAxis: yAxisConfig,
      plotOptions: { scatter: { jitter: variation3Config.jitter } },
      tooltip: {
        useHTML: true,
        backgroundColor: "white",
        borderColor: "#A9A9A9",
        borderRadius: 7,
        style: { fontFamily: "'Roboto Flex'" },
        headerFormat:
          `<span style="font-size: 14px; font-weight: 600">{series.name}</span><br/>` +
          `<span style="color: #E96926;">─────</span><br/>`,
        pointFormatter: function () {
          return `<div>
                      <div style="color: #64748B; font-size: 11px;">MEDICARE %</div>
                      <div style="font-size: 16px; font-weight: 700;">
                        ${Highcharts.numberFormat(this.y, 2, ".", ",")}%
                      </div>
                    </div>
                    <div style="margin-top: 10px; color: #64748B; font-size: 11px;">NEGOTIATED RATE</div>
                    <div style="font-size: 15px; font-weight: 600; color: #1E293B;">
                      $${Highcharts.numberFormat(this.negotiated, 2, ".", ",")}
                    </div>
                    <div style="margin-top: 10px; color: #64748B; font-size: 11px;">PROVIDER</div>
                    <div style="font-size: 14px; font-weight: 600; color: #334155;">
                      ${this.provider}
                    </div>`;
        },
      },
      series,
    });
  }, [currentView]);

  const buttonStyle = (active) => ({
    margin: "0 8px",
    padding: "12px 24px",
    backgroundColor: active ? "#3e3e8c" : "#F8FAFC",
    color: active ? "white" : "#3e3e8c",
    border: "1px solid #E2E8F0",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "14px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  });

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      <div style={{ marginBottom: "20px", textAlign: "left" }}>
        <button
          style={buttonStyle(currentView === "all")}
          onClick={() => setCurrentView("all")}
        >
          All Data
        </button>
        <button
          style={buttonStyle(currentView === "low")}
          onClick={() => setCurrentView("low")}
        >
          100-1000
        </button>
        <button
          style={buttonStyle(currentView === "high")}
          onClick={() => setCurrentView("high")}
        >
          1000+
        </button>
      </div>
      <div id="scatter-jitter-container" style={{ height: "700px" }} />
    </div>
  );
};

export default ScatterJitterPlot;
